// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */

import * as React from "react";

import { faro } from "@grafana/faro-react";
import { selectDraftElementId } from "@v1/reducers/core-reducer";
import { useEditorStore } from "@v1/store/store";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { shouldLogAnalytics } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { createContext, useRequiredContext } from "replo-utils/react/context";

const EditorPerformanceContext = createContext<{
  setWasCandidateBoxJustClicked: () => void;
  leftBarElementRef: React.RefObject<HTMLDivElement>;
  rightBarElementRef: React.RefObject<HTMLDivElement>;
}>("EditorPerformanceContext", {
  setWasCandidateBoxJustClicked: () => {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error("setIsCandidateBoxClicked not implemented");
  },
  leftBarElementRef: React.createRef<HTMLDivElement>(),
  rightBarElementRef: React.createRef<HTMLDivElement>(),
});

export function EditorPerformanceProvider({
  children,
}: React.PropsWithChildren) {
  const candidateBoxClickedRef = React.useRef<boolean>(false);
  const perfObserverRef = React.useRef<PerformanceObserver | null>(null);
  const leftBarElementRef = React.useRef<HTMLDivElement | null>(null);
  const rightBarElementRef = React.useRef<HTMLDivElement | null>(null);

  const { user } = useCurrentUser();
  const userId = user?.id;
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  const store = useEditorStore();

  const perfObserver = React.useCallback(
    (list: PerformanceObserverEntryList) => {
      list.getEntries().forEach((entry) => {
        const elementId = selectDraftElementId(store.getState());

        const context = {
          elementId: elementId ?? "",
          userId: userId ?? "",
          projectId: projectId ?? "",
          workspaceId: workspaceId ?? "",
        };

        if (entry.name === "click") {
          if (candidateBoxClickedRef.current) {
            candidateBoxClickedRef.current = false;
            faro.api.pushMeasurement({
              type: "candidate_box_click",
              values: {
                execution_time_ms: entry.duration,
              },
              context,
            });
          } else if (
            leftBarElementRef.current?.contains(
              (entry as PerformanceEventTiming).target,
            )
          ) {
            faro.api.pushMeasurement({
              type: "left_bar_click",
              values: {
                execution_time_ms: entry.duration,
              },
              context,
            });
          } else if (
            rightBarElementRef.current?.contains(
              (entry as PerformanceEventTiming).target,
            )
          ) {
            faro.api.pushMeasurement({
              type: "right_bar_click",
              values: {
                execution_time_ms: entry.duration,
              },
              context,
            });
          }
        }
      });
    },
    [projectId, userId, workspaceId, store],
  );

  React.useEffect(() => {
    if (!shouldLogAnalytics()) {
      return;
    }

    if (perfObserverRef.current) {
      perfObserverRef.current.disconnect();
    }

    perfObserverRef.current = new PerformanceObserver(perfObserver);
    perfObserverRef.current.observe({ entryTypes: ["event"] });

    return () => {
      perfObserverRef.current?.disconnect();
    };
  }, [perfObserver]);

  return (
    <EditorPerformanceContext.Provider
      value={{
        setWasCandidateBoxJustClicked: () => {
          candidateBoxClickedRef.current = true;
        },
        leftBarElementRef,
        rightBarElementRef,
      }}
    >
      {children}
    </EditorPerformanceContext.Provider>
  );
}

export const useEditorPerformanceContext = () => {
  return useRequiredContext(EditorPerformanceContext);
};
